/**
 * Multi-device gameplay screens — New Model.
 * Two views: Reader (card + controls) and Scoreboard (timer + scores + advance).
 * reader+scoreboard mode combines both on a single device.
 */

import * as ws from '../ws-client';
import { SyncedGameState } from '../../shared/protocol';
import { ROUND_RULES, RoundNumber } from '../../shared/types';
import { standardDecks } from '../../data';
import { navigate } from '../router';
import { renderMultiDeckFromState, resetMultiDeckState } from './multi-custom-deck';
import { t, getRoundName, getRoundDesc, tCard, tDeck } from '../i18n';

let stateUnsub: (() => void) | null = null;
let eventUnsub: (() => void) | null = null;
let currentState: SyncedGameState | null = null;

/** Called by multi-lobby when transitioning away from lobby phase.
 *  Seeds the initial state so the first render has data without waiting for a new state-update. */
export function setInitialState(state: SyncedGameState): void {
  currentState = state;
}

function ensureListeners(): void {
  if (stateUnsub) return;
  stateUnsub = ws.onStateUpdate((state) => {
    currentState = state;
    clearActionPending(); // Server confirmed — re-enable buttons
    if (state.phase === 'turn-active' && multiTurnTimer !== null) {
      // If server says timer stopped (reader disconnected), clear local timer
      if (!state.timerRunning) {
        clearMultiTurnTimer();
      }
      renderTurnActiveUI();
    } else {
      routeByPhase(state);
    }
  });

  // Listen for locale-changed re-render requests (safe re-render without losing session)
  window.addEventListener('multi-rerender', () => {
    if (currentState) {
      routeByPhase(currentState);
    }
  });
}

function routeByPhase(state: SyncedGameState): void {
  // 2-phone grace period: if peer disconnected, show waiting overlay instead of normal phase
  if (state.peerDisconnected) {
    clearMultiTurnTimer();
    renderPeerDisconnectedOverlay(state);
    return;
  }

  switch (state.phase) {
    case 'setup': renderMultiSetup(); break;
    case 'deck-creation': renderMultiDeckFromState(state); break;
    case 'round-interstitial': clearMultiTurnTimer(); resetMultiDeckState(); renderMultiRoundInterstitial(); break;
    case 'turn-waiting': clearMultiTurnTimer(); renderMultiTurnWaiting(); break;
    case 'turn-active': renderMultiTurnActive(); break;
    case 'mid-round-transition': clearMultiTurnTimer(); renderMultiMidRoundTransition(); break;
    case 'turn-end': clearMultiTurnTimer(); renderMultiTurnEnd(); break;
    case 'round-complete': clearMultiTurnTimer(); renderMultiRoundComplete(); break;
    case 'game-end': clearMultiTurnTimer(); renderMultiGameEnd(); break;
  }
}

// --- Peer Disconnected Overlay (2-phone grace period) ---

function renderPeerDisconnectedOverlay(state: SyncedGameState): void {
  const app = document.getElementById('app')!;
  const elapsed = state.peerDisconnectedAt
    ? Math.floor((Date.now() - state.peerDisconnectedAt) / 1000)
    : 0;
  const showContinueSolo = elapsed >= 30;

  app.innerHTML = `
    <div class="stack center" style="padding: var(--space-xl);">
      <div class="surface" style="border: 2px solid var(--color-warning); padding: var(--space-lg); text-align: center; width: 100%;">
        <p style="font-weight: var(--font-weight-bold); color: var(--color-warning); font-size: var(--font-size-xl);">
          ${t('readerDisconnected')}
        </p>
        <p class="text-muted" style="margin-top: var(--space-md); font-size: var(--font-size-md);">
          ${t('waitingForReaderReconnect')}
        </p>
        <p class="text-muted" style="margin-top: var(--space-sm); font-size: var(--font-size-sm);">
          ${t('round')} ${state.currentRound} &middot; ${t('team', state.currentTeam)}
        </p>
        <div class="score-bar" style="margin-top: var(--space-md);">
          <div><span class="team-label">${t('teamA')}</span><span class="team-score">&nbsp;${state.scores.A}</span></div>
          <div><span class="team-score">${state.scores.B}&nbsp;</span><span class="team-label">${t('teamB')}</span></div>
        </div>
      </div>
      ${showContinueSolo ? `
        <button class="btn btn-primary" id="btn-continue-solo" style="margin-top: var(--space-lg);">
          ${t('continue')} Solo
        </button>
      ` : `
        <p class="text-muted" style="margin-top: var(--space-lg); font-size: var(--font-size-xs);">
          ${t('continue')} Solo option available in ${30 - elapsed}s...
        </p>
      `}
    </div>
  `;

  document.getElementById('btn-continue-solo')?.addEventListener('click', () => {
    ws.sendMessage({ type: 'continue-solo' });
  });

  // Auto-refresh countdown display every 5 seconds if not yet showing button
  if (!showContinueSolo) {
    setTimeout(() => {
      if (currentState?.peerDisconnected) renderPeerDisconnectedOverlay(currentState);
    }, 5000);
  }
}

function isReader(): boolean {
  return currentState?.myDeviceRole === 'reader' || currentState?.myDeviceRole === 'reader+scoreboard';
}

function isScoreboard(): boolean {
  return currentState?.myDeviceRole === 'scoreboard' || currentState?.myDeviceRole === 'reader+scoreboard';
}

function isHostDevice(): boolean {
  return currentState?.isHost ?? false;
}

// --- Setup Screen ---

export function renderMultiSetup(): void {
  ensureListeners();
  const app = document.getElementById('app')!;
  if (!currentState) { app.innerHTML = `<p class="text-muted center">${t('loading')}</p>`; return; }

  // If we're not actually in setup phase, route to the correct screen
  // (this happens on rejoin when navigate('multi-setup') is used as the entry point)
  if (currentState.phase !== 'setup') {
    routeByPhase(currentState);
    return;
  }

  if (isHostDevice()) {
    const settings = currentState.settings;
    app.innerHTML = `
      <h1>${t('gameSetup')}</h1>
      <p class="subtitle">${t('setupSubtitle')}</p>
      <div class="stack-lg">
        <div class="setting-group">
          <p class="setting-label">${t('chooseDeck')}</p>
          <div class="stack-sm" id="deck-list">
            ${standardDecks.map((deck) => `
              <label class="deck-option ${settings.deckId === deck.id ? 'selected' : ''}" data-deck-id="${deck.id}">
                <input type="radio" name="deck" value="${deck.id}" ${settings.deckId === deck.id ? 'checked' : ''} />
                <div class="deck-meta">
                  <div class="deck-name">${deck.name}</div>
                  <div class="deck-desc">${tDeck(deck.id, deck.description)} (${deck.cards.length} ${t('cards')})</div>
                </div>
              </label>
            `).join('')}
            <label class="deck-option ${settings.deckId === 'custom' ? 'selected' : ''}" data-deck-id="custom">
              <input type="radio" name="deck" value="custom" ${settings.deckId === 'custom' ? 'checked' : ''} />
              <div class="deck-meta">
                <div class="deck-name">${t('customDeck')}</div>
                <div class="deck-desc">${t('customDeckDesc')}</div>
              </div>
            </label>
          </div>
        </div>
        <div class="setting-group">
          <p class="setting-label">${t('timerPerTurn')}</p>
          <div class="toggle-group">
            <button class="toggle-btn ${settings.timerSeconds === 30 ? 'active' : ''}" data-timer="30">30s</button>
            <button class="toggle-btn ${settings.timerSeconds === 45 ? 'active' : ''}" data-timer="45">45s</button>
            <button class="toggle-btn ${settings.timerSeconds === 60 ? 'active' : ''}" data-timer="60">60s</button>
            <button class="toggle-btn ${settings.timerSeconds === 90 ? 'active' : ''}" data-timer="90">90s</button>
          </div>
        </div>
        <div class="setting-group">
          <p class="setting-label">${t('cardsPerPlayer')}</p>
          <div class="toggle-group">
            <button class="toggle-btn ${settings.cardsPerPlayer === 4 ? 'active' : ''}" data-cpp="4">4</button>
            <button class="toggle-btn ${settings.cardsPerPlayer === 5 ? 'active' : ''}" data-cpp="5">5</button>
            <button class="toggle-btn ${settings.cardsPerPlayer === 6 ? 'active' : ''}" data-cpp="6">6</button>
            <button class="toggle-btn ${settings.cardsPerPlayer === 7 ? 'active' : ''}" data-cpp="7">7</button>
          </div>
        </div>
        <div class="setting-group">
          <p class="setting-label">${t('playerOrder')}</p>
          <div class="toggle-group">
            <button class="toggle-btn ${settings.playerOrder === 'shuffle' ? 'active' : ''}" data-order="shuffle">${t('shuffle')}</button>
            <button class="toggle-btn ${settings.playerOrder === 'define' ? 'active' : ''}" data-order="define">${t('define')}</button>
          </div>
        </div>
        <label class="checkbox-row">
          <input type="checkbox" id="drawing-toggle" ${settings.drawingRoundEnabled ? 'checked' : ''} />
          ${t('enableRound4')}
        </label>
        <p id="setup-error" class="error-text" style="min-height: 1.2em;"></p>
        <button class="btn btn-primary" id="btn-confirm">${t('confirmAndPlay')}</button>
      </div>
    `;

    document.querySelectorAll('.deck-option').forEach((opt) => {
      opt.addEventListener('click', () => {
        const deckId = (opt as HTMLElement).dataset.deckId!;
        if (deckId === 'custom') { ws.updateSettings({ deckId: 'custom' }); }
        else { ws.selectDeck(deckId); }
      });
    });
    document.querySelectorAll('[data-timer]').forEach((btn) => {
      btn.addEventListener('click', () => ws.updateSettings({ timerSeconds: parseInt((btn as HTMLElement).dataset.timer!) }));
    });
    document.querySelectorAll('[data-cpp]').forEach((btn) => {
      btn.addEventListener('click', () => ws.updateSettings({ cardsPerPlayer: parseInt((btn as HTMLElement).dataset.cpp!) }));
    });
    document.querySelectorAll('[data-order]').forEach((btn) => {
      btn.addEventListener('click', () => ws.updateSettings({ playerOrder: (btn as HTMLElement).dataset.order }));
    });
    document.getElementById('drawing-toggle')?.addEventListener('change', (e) => {
      ws.updateSettings({ drawingRoundEnabled: (e.target as HTMLInputElement).checked });
    });
    document.getElementById('btn-confirm')?.addEventListener('click', () => {
      if (currentState?.settings.deckId === 'custom') { ws.startCustomDeck(); }
      else { ws.startGame(); }
    });
  } else {
    // Non-host sees read-only summary
    const settings = currentState.settings;
    const deckName = standardDecks.find((d) => d.id === settings.deckId)?.name || t('notSelected');
    app.innerHTML = `
      <h1>${t('gameSetup')}</h1>
      <p class="subtitle">${t('hostIsChoosing')}</p>
      <div class="surface stack-sm" style="text-align: left;">
        <div class="turn-summary-stat"><span>${t('deck')}</span><span class="stat-value">${deckName}</span></div>
        <div class="turn-summary-stat"><span>${t('timer')}</span><span class="stat-value">${settings.timerSeconds}s</span></div>
        <div class="turn-summary-stat"><span>${t('rounds')}</span><span class="stat-value">${settings.drawingRoundEnabled ? 4 : 3}</span></div>
      </div>
    `;
  }
}

// --- Round Interstitial ---

export function renderMultiRoundInterstitial(): void {
  ensureListeners();
  const app = document.getElementById('app')!;
  if (!currentState) return;

  const round = currentState.currentRound;
  const readerName = currentState.currentCardReaderName;

  if (isReader()) {
    // Reader: show round info + "Pass to [Player]"
    app.innerHTML = `
      <div class="round-interstitial">
        <div class="round-number">${t('roundN', round)}</div>
        <div class="round-name">"${getRoundName(round)}"</div>
        <div class="round-description">${getRoundDesc(round)}</div>
        <div class="stack" style="margin-top: var(--space-xl);">
          <p class="text-secondary" style="font-size: var(--font-size-lg);">
            ${t('passPhoneTo')} <strong>${readerName || '...'}</strong> (${t('team', currentState.currentTeam)})
          </p>
        </div>
      </div>
    `;
  } else {
    // Scoreboard: show round info + advance button (host only)
    app.innerHTML = `
      <div class="round-interstitial">
        <div class="round-number">${t('roundN', round)}</div>
        <div class="round-name">"${getRoundName(round)}"</div>
        <div class="round-description">${getRoundDesc(round)}</div>
        <div class="stack" style="margin-top: var(--space-xl);">
          <p class="text-muted">${t('firstUp')} <strong>${readerName || '...'}</strong> (${t('team', currentState.currentTeam)})</p>
          ${isHostDevice() ? `
            <button class="btn btn-primary" id="btn-advance">${t('letsGo')}</button>
          ` : `
            <p class="text-muted">${t('waitingForHost')}</p>
          `}
        </div>
      </div>
    `;
    document.getElementById('btn-advance')?.addEventListener('click', () => ws.advanceToTurnWaiting());
  }
}

// --- Turn Waiting ---

export function renderMultiTurnWaiting(): void {
  ensureListeners();
  const app = document.getElementById('app')!;
  if (!currentState) return;

  const readerName = currentState.currentCardReaderName;
  const round = currentState.currentRound;

  if (isReader()) {
    // Reader device: show who should have it + START button
    app.innerHTML = `
      <div class="round-interstitial">
        <p class="text-muted" style="margin-bottom: var(--space-sm);">${t('round')} ${round}: ${getRoundName(round)}</p>
        <h1 style="font-size: var(--font-size-xl);">${readerName || t('loading')}</h1>
        <p class="text-secondary">${t('team', currentState.currentTeam)}</p>
        <p class="text-muted" style="margin: var(--space-lg) 0;">${t('pressStartWhenReady')}</p>
        <button class="btn btn-primary" id="btn-start" style="font-size: var(--font-size-xl); padding: var(--space-lg);">${t('start')}</button>
      </div>
    `;
    document.getElementById('btn-start')?.addEventListener('click', () => ws.startTurn());
  } else {
    // Scoreboard: waiting indicator
    app.innerHTML = `
      <div class="round-interstitial">
        <p class="text-muted" style="margin-bottom: var(--space-sm);">${t('round')} ${round}: ${getRoundName(round)}</p>
        <h1 style="font-size: var(--font-size-xl);">${t('nextUpColon')}</h1>
        <div class="round-name">${readerName || t('loading')}</div>
        <p class="text-secondary">${t('team', currentState.currentTeam)}</p>
        <p class="text-muted" style="margin-top: var(--space-lg);">${t('waitingForThemToStart')}</p>
        ${isHostDevice() ? `
          <button class="btn btn-secondary" id="btn-host-start" style="margin-top: var(--space-lg);">
            ${t('startForThem')}
          </button>
        ` : ''}
      </div>
    `;
    document.getElementById('btn-host-start')?.addEventListener('click', () => ws.startTurn());
  }
}

// --- Turn Active ---

let multiTurnTimer: number | null = null;
let localTimerRemaining: number = 0;
let actionPending: boolean = false; // Debounce guard: prevents double-tap on Next/Skip/Back

function clearMultiTurnTimer(): void {
  if (multiTurnTimer !== null) {
    clearInterval(multiTurnTimer);
    multiTurnTimer = null;
  }
}

/** Reset the action pending flag when a state-update arrives (server confirmed action) */
function clearActionPending(): void {
  actionPending = false;
}

export function renderMultiTurnActive(): void {
  ensureListeners();
  if (!currentState) return;

  if (multiTurnTimer === null && currentState.timerRunning) {
    localTimerRemaining = currentState.timerRemaining;
    startMultiTurnTimer();
  }

  renderTurnActiveUI();
}

function startMultiTurnTimer(): void {
  clearMultiTurnTimer();
  multiTurnTimer = window.setInterval(() => {
    localTimerRemaining--;
    if (localTimerRemaining <= 0) {
      clearMultiTurnTimer();
      localTimerRemaining = 0;
      // Only the Reader sends timer-expired
      if (isReader()) {
        ws.sendMessage({ type: 'timer-expired' });
      }
    }
    renderTurnActiveUI();
  }, 1000);
}

function renderTurnActiveUI(): void {
  const app = document.getElementById('app')!;
  if (!currentState) return;

  if (currentState.phase !== 'turn-active') {
    clearMultiTurnTimer();
    routeByPhase(currentState);
    return;
  }

  const state = currentState;
  const round = state.currentRound;
  const timerValue = localTimerRemaining > 0 ? localTimerRemaining : state.timerRemaining;
  let timerClass = 'timer-display';
  if (timerValue <= 10) timerClass += ' danger';
  else if (timerValue <= 20) timerClass += ' warning';
  const minutes = Math.floor(timerValue / 60);
  const seconds = timerValue % 60;
  const timerText = `${minutes}:${String(seconds).padStart(2, '0')}`;

  if (isReader()) {
    // Reader View: card + controls
    const card = state.currentCard;
    const pointStars = card ? '\u2605'.repeat(card.points) + '\u2606'.repeat(4 - card.points) : '';
    const disabledAttr = actionPending ? 'disabled' : '';
    const pendingClass = actionPending ? ' action-pending' : '';

    app.innerHTML = `
      <div class="turn-layout${pendingClass}">
        <div class="row" style="justify-content: space-between;">
          <div class="${timerClass}">${timerText}</div>
          <span class="badge">R${round}: ${getRoundName(round)}</span>
        </div>
        ${card ? `
          <div class="game-card">
            <div class="card-category">${card.category}</div>
            <div class="card-title">${card.title}</div>
            <div class="card-description">${tCard(card.id, card.description)}</div>
            <div class="card-points">${pointStars} (${card.points} ${card.points > 1 ? t('pts') : t('pt')})</div>
          </div>
        ` : `<div class="surface center"><p class="text-muted">${t('loadingCard')}</p></div>`}
        <button class="btn btn-primary btn-next-card" id="btn-next" ${disabledAttr}>${t('next')}</button>
        <div class="action-buttons-secondary">
          <button class="btn btn-secondary" id="btn-back" ${disabledAttr}>${t('back')}</button>
          <button class="btn btn-danger" id="btn-skip" ${disabledAttr}>${t('skip')}</button>
        </div>
        <div class="score-bar">
          <div><span class="team-label">${t('teamA')}</span><span class="team-score">&nbsp;${state.scores.A}</span></div>
          <div><span class="team-score">${state.scores.B}&nbsp;</span><span class="team-label">${t('teamB')}</span></div>
        </div>
        <p class="center text-muted" style="font-size: var(--font-size-xs);">${t('cardsRemaining', state.unplayedCardCount, state.unplayedCardCount !== 1 ? 's' : '')}</p>
      </div>
    `;

    document.getElementById('btn-next')?.addEventListener('click', () => {
      if (actionPending) return;
      actionPending = true;
      renderTurnActiveUI(); // Immediately disable buttons visually
      ws.nextCard();
    });
    document.getElementById('btn-skip')?.addEventListener('click', () => {
      if (actionPending) return;
      actionPending = true;
      renderTurnActiveUI();
      ws.skipCard();
    });
    document.getElementById('btn-back')?.addEventListener('click', () => {
      if (actionPending) return;
      actionPending = true;
      renderTurnActiveUI();
      ws.goBack();
    });
  } else {
    // Scoreboard View: timer + scores + optional protest
    const marked = state.markedProtests || [];
    const lastCardProtested = state.lastCorrectCardTitle ? marked.includes(state.lastCorrectCardTitle) : false;

    // Reader disconnect overlay
    const readerGone = state.readerDisconnected;
    const disconnectElapsed = readerGone && state.readerDisconnectedAt
      ? Math.floor((Date.now() - state.readerDisconnectedAt) / 1000)
      : 0;
    const showAssignNewReader = readerGone && disconnectElapsed >= 30 && isHostDevice();

    // Build list of connected devices that could become the new reader
    const assignableDevices = state.devices.filter(d =>
      d.role !== 'reader' && d.role !== 'reader+scoreboard'
    );

    app.innerHTML = `
      <div class="turn-layout">
        ${readerGone ? `
          <div class="surface" style="width: 100%; border: 2px solid var(--color-warning); padding: var(--space-md); margin-bottom: var(--space-md); text-align: center;">
            <p style="font-weight: var(--font-weight-bold); color: var(--color-warning); font-size: var(--font-size-lg);">${t('readerDisconnected')}</p>
            <p class="text-muted" style="margin-top: var(--space-xs);">${t('readerDisconnectedDesc')}</p>
            ${showAssignNewReader ? `
              <p class="text-muted" style="margin-top: var(--space-sm); font-size: var(--font-size-sm);">${t('readerTimeout')}</p>
              <select id="select-new-reader" style="padding: 6px 12px; border-radius: 6px; background: var(--color-surface); color: var(--color-text); border: 1px solid var(--color-border, #444); margin-top: var(--space-xs);">
                ${assignableDevices.map(d => `<option value="${d.connectionId}">${d.label}${d.isHost ? ' (you)' : ''}</option>`).join('')}
              </select>
              <button class="btn btn-primary" id="btn-assign-new-reader" style="margin-top: var(--space-sm);">${t('assignNewReader')}</button>
            ` : `
              <p class="text-muted" style="margin-top: var(--space-sm); font-size: var(--font-size-sm);">
                ${t('waitingForReaderReconnect')} (${30 - disconnectElapsed}s)
              </p>
            `}
          </div>
        ` : ''}
        <div class="${timerClass}" style="font-size: var(--font-size-3xl);">${timerText}</div>
        <p class="text-secondary center">${t('round')} ${round}: ${getRoundName(round)}</p>
        <p class="text-muted center">${t('team', state.currentTeam)} — ${state.currentCardReaderName || ''} ${t('isReading')}</p>

        ${state.lastCorrectCardTitle ? `
          <div class="surface" style="width: 100%;">
            <p class="text-muted" style="font-size: var(--font-size-xs);">${t('lastCorrect')}</p>
            <p style="font-weight: var(--font-weight-bold); font-size: var(--font-size-lg);">"${state.lastCorrectCardTitle}"</p>
            ${lastCardProtested ? `
              <button class="protest-mark-btn protested" disabled>${t('protested')}</button>
            ` : `
              <button class="protest-mark-btn" id="btn-mark-protest">${t('protestThisCard')}</button>
            `}
          </div>
        ` : `
          <div class="surface" style="width: 100%;">
            <p class="text-muted center">${t('waitingForFirstCard')}</p>
          </div>
        `}

        <div class="score-bar">
          <div><span class="team-label">${t('teamA')}</span><span class="team-score">&nbsp;${state.scores.A}</span></div>
          <div><span class="team-score">${state.scores.B}&nbsp;</span><span class="team-label">${t('teamB')}</span></div>
        </div>
        <p class="center text-muted" style="font-size: var(--font-size-xs);">${t('cardsRemaining', state.unplayedCardCount, state.unplayedCardCount !== 1 ? 's' : '')}</p>
      </div>
    `;

    document.getElementById('btn-mark-protest')?.addEventListener('click', () => {
      if (state.lastCorrectCardTitle) { ws.markProtest(state.lastCorrectCardTitle); }
    });

    document.getElementById('btn-assign-new-reader')?.addEventListener('click', () => {
      const select = document.getElementById('select-new-reader') as HTMLSelectElement;
      const targetConnId = select?.value;
      if (targetConnId) {
        ws.assignDeviceRole(targetConnId, 'reader');
      }
    });

    // Auto-refresh the countdown display every 5s while waiting for reader reconnect
    if (readerGone && !showAssignNewReader) {
      setTimeout(() => {
        if (currentState?.readerDisconnected && currentState?.phase === 'turn-active') {
          renderTurnActiveUI();
        }
      }, 5000);
    }
  }
}

// --- Mid-Round Transition ---

function renderMultiMidRoundTransition(): void {
  ensureListeners();
  const app = document.getElementById('app')!;
  if (!currentState) return;

  const round = currentState.currentRound;
  const readerName = currentState.currentCardReaderName;
  const minutes = Math.floor(currentState.timerRemaining / 60);
  const seconds = currentState.timerRemaining % 60;
  const timeText = `${minutes}:${String(seconds).padStart(2, '0')}`;

  if (isReader()) {
    app.innerHTML = `
      <div class="round-interstitial">
        <p class="text-muted">${t('allCardsGuessed')}</p>
        <div class="round-number">${t('roundN', round)}</div>
        <div class="round-name">"${getRoundName(round)}"</div>
        <div class="round-description">${getRoundDesc(round)}</div>
        <div class="surface" style="margin-top: var(--space-lg); padding: var(--space-md);">
          <p class="text-secondary">${t('continuesWith', readerName || '')} <strong>${timeText}</strong> ${t('remaining')}</p>
        </div>
        <button class="btn btn-primary" id="btn-resume" style="margin-top: var(--space-xl); font-size: var(--font-size-xl);">
          ${t('start')}
        </button>
      </div>
    `;
    document.getElementById('btn-resume')?.addEventListener('click', () => ws.resumeMidRound());
  } else {
    app.innerHTML = `
      <div class="round-interstitial">
        <p class="text-muted">${t('allCardsGuessed')}</p>
        <div class="round-number">${t('roundN', round)}</div>
        <div class="round-name">"${getRoundName(round)}"</div>
        <div class="round-description">${getRoundDesc(round)}</div>
        <div class="surface" style="margin-top: var(--space-lg); padding: var(--space-md);">
          <p class="text-secondary">${t('continuesWith', readerName || '')} <strong>${timeText}</strong> ${t('remaining')}</p>
        </div>
        ${isHostDevice() ? `
          <button class="btn btn-primary" id="btn-resume" style="margin-top: var(--space-xl);">${t('start')}</button>
        ` : `
          <p class="text-muted" style="margin-top: var(--space-lg);">${t('waitingForThemToStart')}</p>
        `}
      </div>
    `;
    document.getElementById('btn-resume')?.addEventListener('click', () => ws.resumeMidRound());
  }
}

// --- Turn End ---

export function renderMultiTurnEnd(): void {
  ensureListeners();
  const app = document.getElementById('app')!;
  if (!currentState) return;

  const state = currentState;
  const summary = state.lastTurnSummary;
  const nextName = state.nextCardReaderName;
  const nextTeam = state.nextCardReaderTeam;

  if (isReader()) {
    // Reader: "Time's up! Pass phone to [Next Player]"
    app.innerHTML = `
      <div class="stack center">
        <h1 style="font-size: var(--font-size-xl);">${t('timesUp')}</h1>
        ${summary ? `
          <div class="surface" style="width: 100%;">
            <div class="stack-sm">
              <div class="turn-summary-stat"><span>${t('cardsCorrect')}</span><span class="stat-value">${summary.cardsCorrect}</span></div>
              <div class="turn-summary-stat"><span>${t('pointsEarned')}</span><span class="stat-value" style="color: var(--color-success);">+${summary.pointsEarned}</span></div>
            </div>
          </div>
        ` : ''}
        <div style="margin-top: var(--space-xl); padding: var(--space-lg); border: 2px dashed var(--color-accent); border-radius: var(--radius-lg);">
          <p class="text-secondary" style="font-size: var(--font-size-lg);">
            ${t('passPhoneTo')} <strong>${nextName || '...'}</strong>
            ${nextTeam ? `(${t('team', nextTeam)})` : ''}
          </p>
        </div>
      </div>
    `;
  } else {
    // Scoreboard: full summary + protest resolution + Continue button
    const unresolvedProtests = (state.protests || []).filter((p: any) => !p.resolved);
    const hasUnresolved = unresolvedProtests.length > 0;

    app.innerHTML = `
      <div class="stack">
        <h1 style="font-size: var(--font-size-xl);">${t('timesUp')}</h1>
        ${summary ? `
          <div class="surface">
            <h3>${summary.cardReaderName} (${t('team', summary.team)})</h3>
            <div class="stack-sm" style="margin-top: var(--space-md);">
              <div class="turn-summary-stat"><span>${t('cardsCorrect')}</span><span class="stat-value">${summary.cardsCorrect}</span></div>
              <div class="turn-summary-stat"><span>${t('pointsEarned')}</span><span class="stat-value" style="color: var(--color-success);">+${summary.pointsEarned}</span></div>
              ${summary.cardsSkipped > 0 ? `
                <div class="turn-summary-stat"><span>${t('cardsSkipped')}</span><span class="stat-value">${summary.cardsSkipped}</span></div>
                <div class="turn-summary-stat"><span>${t('pointsLost')}</span><span class="stat-value" style="color: var(--color-danger);">-${summary.pointsLost}</span></div>
              ` : ''}
            </div>
          </div>
        ` : ''}

        ${hasUnresolved && isHostDevice() ? `
          <div class="surface" style="border-left: 3px solid var(--color-warning); padding: var(--space-md);">
            <p class="setting-label" style="color: var(--color-warning); margin-bottom: var(--space-sm);">${t('protestACard')} (${unresolvedProtests.length})</p>
            <div class="stack-sm">
              ${unresolvedProtests.map((p: any, idx: number) => `
                <div class="row" style="justify-content: space-between; align-items: center; padding: var(--space-sm) 0; border-bottom: 1px solid var(--color-border, #333);">
                  <span style="font-weight: var(--font-weight-bold);">"${p.cardTitle}" (${p.cardPoints}${t('pt')})</span>
                  <div style="display: flex; gap: var(--space-xs);">
                    <button class="btn btn-danger" data-protest-idx="${idx}" data-decision="upheld" style="padding: 4px 12px; font-size: var(--font-size-xs);">${t('invalid')}</button>
                    <button class="btn btn-secondary" data-protest-idx="${idx}" data-decision="dismissed" style="padding: 4px 12px; font-size: var(--font-size-xs);">${t('valid')}</button>
                  </div>
                </div>
              `).join('')}
            </div>
          </div>
        ` : ''}

        ${hasUnresolved && !isHostDevice() ? `
          <div class="surface" style="border-left: 3px solid var(--color-warning); padding: var(--space-md);">
            <p class="text-muted">${t('protestACard')} (${unresolvedProtests.length}) — ${t('waitingForHost')}</p>
          </div>
        ` : ''}

        <div class="score-bar">
          <div><span class="team-label">${t('teamA')}</span><span class="team-score">&nbsp;${state.scores.A}</span></div>
          <div><span class="team-score">${state.scores.B}&nbsp;</span><span class="team-label">${t('teamB')}</span></div>
        </div>
        ${isHostDevice() ? `
          <button class="btn btn-primary" id="btn-continue">${t('continue')}</button>
        ` : `
          <p class="text-muted center">${t('waitingForHost')}</p>
        `}
      </div>
    `;

    // Protest resolution buttons
    document.querySelectorAll('[data-protest-idx]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const idx = parseInt((btn as HTMLElement).dataset.protestIdx!);
        const decision = (btn as HTMLElement).dataset.decision as 'upheld' | 'dismissed';
        ws.hostResolveProtest(idx, decision);
      });
    });

    document.getElementById('btn-continue')?.addEventListener('click', () => ws.skipProtests());
  }
}

// --- Round Complete ---

export function renderMultiRoundComplete(): void {
  ensureListeners();
  const app = document.getElementById('app')!;
  if (!currentState) return;

  const state = currentState;
  const round = state.currentRound;
  const roundScore = state.roundScores[round] || { A: 0, B: 0 };

  app.innerHTML = `
    <div class="stack center">
      <h1 style="font-size: var(--font-size-xl);">${t('roundComplete', round)}</h1>
      <p class="text-muted">"${getRoundName(round)}"</p>
      <div class="final-scores">
        <div class="final-score-team"><div class="score-label">${t('teamA')}</div><div class="score-value">+${roundScore.A}</div></div>
        <div class="final-score-team"><div class="score-label">${t('teamB')}</div><div class="score-value">+${roundScore.B}</div></div>
      </div>
      <div class="score-bar">
        <div><span class="team-label">${t('totalA')}</span><span class="team-score">&nbsp;${state.scores.A}</span></div>
        <div><span class="team-score">${state.scores.B}&nbsp;</span><span class="team-label">${t('totalB')}</span></div>
      </div>
      ${isHostDevice() ? `
        <button class="btn btn-primary" id="btn-next-round">${t('nextRound')}</button>
      ` : `
        <p class="text-muted">${t('waitingForHost')}</p>
      `}
    </div>
  `;

  document.getElementById('btn-next-round')?.addEventListener('click', () => ws.advanceRound());
}

// --- Game End ---

export function renderMultiGameEnd(): void {
  ensureListeners();
  const app = document.getElementById('app')!;
  if (!currentState) return;

  const state = currentState;
  let winnerText: string;
  if (state.scores.A > state.scores.B) winnerText = t('teamAWins');
  else if (state.scores.B > state.scores.A) winnerText = t('teamBWins');
  else winnerText = t('itsATie');

  const rounds = state.settings.rounds || 3;
  const roundRows = Array.from({ length: rounds }, (_, i) => i + 1)
    .map((r) => {
      const rs = state.roundScores[r] || { A: 0, B: 0 };
      return `<tr><td>${t('round')} ${r}</td><td>${rs.A}</td><td>${rs.B}</td></tr>`;
    }).join('');

  app.innerHTML = `
    <div class="stack center">
      <h1>${t('gameOver')}</h1>
      <div class="winner-display">${winnerText}</div>
      <div class="final-scores">
        <div class="final-score-team"><div class="score-label">${t('teamA')}</div><div class="score-value">${state.scores.A}</div></div>
        <div class="final-score-team"><div class="score-label">${t('teamB')}</div><div class="score-value">${state.scores.B}</div></div>
      </div>
      <table class="round-breakdown surface">
        <thead><tr><th>${t('round')}</th><th>${t('teamA')}</th><th>${t('teamB')}</th></tr></thead>
        <tbody>${roundRows}</tbody>
      </table>
      <button class="btn btn-primary" id="btn-menu">${t('mainMenu')}</button>
    </div>
  `;

  document.getElementById('btn-menu')?.addEventListener('click', () => {
    ws.leaveSession();
    navigate('menu');
  });
}
